import { createRoute, lazyRouteComponent, type AnyRoute } from "@tanstack/react-router";
import { z } from "zod";

const listSearch = z.object({
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(10).max(200).optional(),
  q: z.string().optional(),
  sort: z.string().optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  status: z.string().optional(),
  companyId: z.coerce.number().optional(),
  tradingPartnerId: z.coerce.number().optional(),
  direction: z.string().optional(),
  documentType: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  view: z.string().optional(),
  tab: z.string().optional(),
}).passthrough();
export type ListSearch = z.infer<typeof listSearch>;

export function adminRoutes(parent: AnyRoute): AnyRoute[] {
  const r = (path: string, load: () => Promise<{ default: React.ComponentType }>, opts: { search?: boolean } = {}) =>
    createRoute({
      getParentRoute: () => parent,
      path,
      component: lazyRouteComponent(load),
      ...(opts.search ? { validateSearch: (s: Record<string, unknown>) => listSearch.parse(s) } : {}),
    });

  const masterData = r("/master-data", () => import("./pages/master-data/layout.tsx"));
  masterData.addChildren([
    createRoute({ getParentRoute: () => masterData, path: "/", component: lazyRouteComponent(() => import("./pages/master-data/products.tsx")), validateSearch: (s: Record<string, unknown>) => listSearch.parse(s) }),
    createRoute({ getParentRoute: () => masterData, path: "/products", component: lazyRouteComponent(() => import("./pages/master-data/products.tsx")), validateSearch: (s: Record<string, unknown>) => listSearch.parse(s) }),
    createRoute({ getParentRoute: () => masterData, path: "/products/$id", component: lazyRouteComponent(() => import("./pages/master-data/product-detail.tsx")) }),
    createRoute({ getParentRoute: () => masterData, path: "/locations", component: lazyRouteComponent(() => import("./pages/master-data/locations.tsx")) }),
    createRoute({ getParentRoute: () => masterData, path: "/number-ranges", component: lazyRouteComponent(() => import("./pages/master-data/number-ranges.tsx")) }),
    createRoute({ getParentRoute: () => masterData, path: "/price-lists", component: lazyRouteComponent(() => import("./pages/master-data/price-lists.tsx")) }),
    createRoute({ getParentRoute: () => masterData, path: "/bookings", component: lazyRouteComponent(() => import("./pages/master-data/bookings.tsx")), validateSearch: (s: Record<string, unknown>) => listSearch.parse(s) }),
    createRoute({ getParentRoute: () => masterData, path: "/pods", component: lazyRouteComponent(() => import("./pages/master-data/pods.tsx")), validateSearch: (s: Record<string, unknown>) => listSearch.parse(s) }),
    createRoute({ getParentRoute: () => masterData, path: "/non-compliance", component: lazyRouteComponent(() => import("./pages/master-data/non-compliance.tsx")), validateSearch: (s: Record<string, unknown>) => listSearch.parse(s) }),
  ]);

  return [
    r("/", () => import("./pages/dashboard.tsx")),
    r("/companies", () => import("./pages/companies.tsx"), { search: true }),
    r("/companies/$id", () => import("./pages/company-detail.tsx"), { search: true }),
    r("/users", () => import("./pages/users.tsx"), { search: true }),
    r("/sftp-accounts", () => import("./pages/sftp-accounts.tsx"), { search: true }),
    r("/wms", () => import("./pages/wms.tsx"), { search: true }),
    r("/transport", () => import("./pages/transport.tsx"), { search: true }),
    r("/settings", () => import("./pages/settings.tsx"), { search: true }),
    r("/audit", () => import("./pages/audit.tsx"), { search: true }),
    r("/feedback", () => import("./pages/feedback.tsx"), { search: true }),
    r("/staff", () => import("./pages/staff-desk.tsx"), { search: true }),
    r("/ai", () => import("./pages/ai-helper.tsx")),
    r("/documents", () => import("./pages/documents.tsx"), { search: true }),
    r("/documents/$id", () => import("./pages/document-detail.tsx"), { search: true }),
    r("/document-types", () => import("./pages/document-types.tsx")),
    r("/partners", () => import("./pages/partners.tsx"), { search: true }),
    r("/partners/new", () => import("./pages/partner-wizard.tsx"), { search: true }),
    r("/partners/$id", () => import("./pages/partner-detail.tsx"), { search: true }),
    r("/partners/$id/setup", () => import("./pages/partner-wizard.tsx"), { search: true }),
    r("/catalogue", () => import("./pages/catalogue.tsx"), { search: true }),
    r("/maps", () => import("./pages/maps.tsx"), { search: true }),
    r("/maps/$id", () => import("./pages/map-editor.tsx"), { search: true }),
    r("/routes", () => import("./pages/routes.tsx"), { search: true }),
    masterData,
    r("/shipments", () => import("./pages/shipments.tsx"), { search: true }),
    r("/shipments/$id", () => import("./pages/asn-builder.tsx"), { search: true }),
    r("/labels", () => import("./pages/labels.tsx"), { search: true }),
    r("/labels/$id", () => import("./pages/label-designer.tsx")),
    r("/paperwork", () => import("./pages/paperwork.tsx"), { search: true }),
    r("/paperwork/$id", () => import("./pages/paperwork-designer.tsx")),
    r("/tools", () => import("./pages/tools.tsx")),
    r("/tools/$slug", () => import("./pages/tool.tsx")),
    r("/alerts", () => import("./pages/alerts.tsx"), { search: true }),
    r("/notifications", () => import("./pages/notifications.tsx"), { search: true }),
    r("/reports", () => import("./pages/reports.tsx"), { search: true }),
    r("/scheduler", () => import("./pages/scheduler.tsx"), { search: true }),
    r("/as2", () => import("./pages/as2.tsx"), { search: true }),
    r("/api-clients", () => import("./pages/api-clients.tsx"), { search: true }),
    r("/webhooks", () => import("./pages/webhooks.tsx"), { search: true }),
    r("/billing", () => import("./pages/billing.tsx"), { search: true }),
    r("/onboarding", () => import("./pages/onboarding.tsx")),
    r("/help/asn", () => import("../shared/pages/help-asn.tsx")),
    r("/account", () => import("../shared/pages/account.tsx"), { search: true }),
  ];
}
